import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  Button,
} from '@mui/material';
import {
  Home,
  FiberNew,
  BugReport,
  Security,
  Speed,
  Update,
  Search,
  CheckCircle,
  Build,
  DeleteOutline,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';

interface ChangelogEntry {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  features: string[];
  improvements: string[];
  bugFixes: string[];
  security?: string[];
  deprecated?: string[];
  breaking?: string[];
}

const ChangelogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const latestRef = React.useRef<HTMLDivElement>(null);

  const scrollToLatest = () => {
    latestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changelogData: ChangelogEntry[] = [
    {
      version: '4.1.1',
      date: '2026-07-28',
      type: 'patch',
      features: [
        'You can drag the Customize button anywhere on the screen — it remembers where you left it',
        'Integration cards show clear placeholder logos when an app image cannot be loaded',
        'Choose a custom start and end date for SLA & Performance reports in Settings',
        'Updated Converse.AI branding on login and in the sidebar, with a clearer handshake icon',
        'Browser tab icon updated to the Converse handshake',
        'Optional demo login details can be shown or hidden on the login page for trial setups',
      ],
      improvements: [
        'Changelog “Current Version” always matches the latest release in the list',
        'Page content no longer sits underneath the floating Customize button',
        'Dialogs and pop-ups follow your light or dark theme more consistently',
        'Floating sidebar looks correct in dark mode without light edges showing through',
        'Logout, Remove, and Delete actions are easier to see in dark mode',
        'Login, forgot password, and reset password screens match your selected theme',
        'Tighter spacing between the handshake icon and Converse.AI name on the login page',
        'Login with Google and account sign-up prompts are hidden on the login page for now',
        'Cleaned up unused frontend code to keep the app leaner and easier to maintain',
      ],
      bugFixes: [
        'Fixed a layout warning on the AI dashboard that could affect page stability',
        'Fixed the AI Agent Hub header button only opening once until you refreshed',
        'Fixed Settings staying light-coloured when dark mode was on',
        'Fixed the SLA custom date range not opening a date picker',
        'Fixed Changelog colours ignoring dark mode',
        'Fixed the login logo being hard to see in dark mode',
        'Fixed the sidebar logo looking too small or nearly invisible',
        'Fixed brand icons not switching properly between light and dark mode',
        'Fixed the Converse.AI name being hard to read on the login page',
        'Fixed the browser tab icon looking wrong or blank',
        'Fixed missing logos on the login page and sidebar',
        'Fixed a large empty gap under the handshake on the login page',
      ],
    },
    {
      version: '4.1.0',
      date: '2026-07-24',
      type: 'minor',
      features: [
        'Knowledge Base and AI Studio are available under a dedicated ConverseFlow section of the product',
        'AI Studio opens reliably from your company domain with secure HTTPS links',
        'Easier setup guides for running Converse locally or in hosted environments',
      ],
      improvements: [
        'AI Studio opens in a cleaner light theme by default when embedded in Converse',
        'Optional controls to simplify the AI Studio header for a more focused experience',
        'Smoother routing so AI Studio stays available behind your main site address',
      ],
      bugFixes: [
        'Fixed Email Templates failing to load on older databases',
        'Fixed email signature settings not working after a database restore',
        'Fixed AI Studio sometimes showing an error page behind a secure proxy',
        'Fixed AI Studio login sending people to the wrong Converse login page',
        'Fixed AI Studio opening with an insecure or incorrect address in some setups',
        'Fixed knowledge-base sign-in failures caused by mismatched Redis passwords',
      ],
    },
    {
      version: '4.0.0',
      date: '2026-03-27',
      type: 'major',
      features: [
        'Refreshed dashboard with a collapsible sidebar and clearer navigation groups',
        'Live theme customizer so you can switch colours and look without leaving the app',
        'Contacts page with a modern table, filters, sorting, and bulk actions',
        'Inboxes page redesigned for easier inbox management',
        'WhatsApp Templates & Sequences with a clearer, more consistent interface',
        'Conversations page actions updated for a simpler download and menu experience',
        'Sticky top bar with search, notifications, AI agent access, and your profile menu',
      ],
      improvements: [
        'More consistent look and feel across the whole platform',
        'Sidebar can collapse to icons, and menu groups expand or collapse as you need',
        'Dark and light mode work reliably across screens',
        'Better layout on phones and tablets, with a slide-out sidebar on mobile',
        'Clearer typography and active-page highlighting in the menu',
        'Voice and video call widgets stay available while you move between pages',
      ],
      bugFixes: [],
      breaking: [
        'Dashboard layout has changed — you will see a new sidebar and updated page designs',
      ],
    },
    {
      version: '3.5.0',
      date: '2026-03-12',
      type: 'minor',
      features: [
        'Voice calling from the web widget and agent dashboard (inbound and outbound)',
        'AI voice agent that can greet and handle calls with speech recognition and replies',
        'Automatic call recording with live transcription',
        'Smooth handoff from the AI voice agent to a human agent during a call',
        'Video calling for agents and customers, with camera and microphone controls',
        'AI video avatar responses for a more natural video experience',
        'Conference calls, screen sharing, supervisor tools, voicemail, and call analytics',
      ],
      improvements: [
        'More reliable call connections, including support for complex networks',
        'AI can answer calls automatically when no agent is available',
        'Hold music and wait-time information for callers in the queue',
        'Call history and recordings appear in the conversation timeline',
        'Admin settings to configure AI voice and video behaviour',
        'Unified call hub you can drag around the screen (AI Agent and Direct Call)',
        'Incoming call alerts stay visible even when you change pages',
      ],
      bugFixes: [],
    },
    {
      version: '3.4.5',
      date: '2026-02-18',
      type: 'patch',
      features: [
        'Slack integration with two-way sync, events, and slash commands',
        'Richer pre-chat forms with custom fields, validation, and routing',
        'Business hours with automatic out-of-hours replies',
        'Live notifications delivered in real time',
      ],
      improvements: [
        'Guided Slack setup with channel mapping and conversation triggers',
        'Easier pre-chat form builder with more field types and routing options',
        'Business hours that respect your timezone for auto-replies',
        'Notifications dropdown updates instantly as new alerts arrive',
        'You can load more notifications at once when reviewing your inbox',
      ],
      bugFixes: [
        'Fixed live notifications dropping when the connection briefly disconnected',
        'Fixed Slack events not linking correctly to conversations',
        'Fixed business hours auto-replies not applying outside the set schedule',
        'Fixed pre-chat form validation and routing edge cases',
      ],
    },
    {
      version: '3.4.0',
      date: '2026-01-27',
      type: 'minor',
      features: [
        'Start new WhatsApp conversations with contacts using approved templates',
        'Start a WhatsApp chat directly from a contact’s profile',
        'Visual mapping of contact details into template placeholders',
        'Full-screen view to browse and manage all notifications',
        'Dedicated page to manage WhatsApp Business templates',
        'Live preview of templates with sample personalization',
      ],
      improvements: [
        'Notifications dropdown includes a clearer “View All” option',
        'Clicking a notification takes you to the related conversation or settings',
        'Account-related notifications open the right profile or settings page',
        'You can review a larger set of notifications in one view',
        'WhatsApp Templates page organized into Templates and Sequences tabs',
        'WhatsApp channel is detected automatically from your inboxes',
        'Contact WhatsApp opt-in status and last opt-in time are easier to see',
        'Clearer multi-step flow when starting a WhatsApp conversation',
      ],
      bugFixes: [
        'Fixed notification clicks opening the wrong page',
        'Fixed some notification types not being recognized correctly',
        'Fixed template layout issues when starting a conversation',
        'Fixed WhatsApp inbox type matching in some accounts',
        'Fixed WhatsApp channel selection when starting a conversation',
      ],
    },
    {
      version: '3.3.0',
      date: '2026-01-19',
      type: 'minor',
      features: [
        'Full WhatsApp template management, from draft through approval',
        'Template builder with Marketing, Utility, and Authentication categories',
        'Templates in multiple languages with personalization placeholders',
        'Headers, body, footer, and buttons supported in templates',
        '24-hour WhatsApp messaging window tracking',
        'Clear session status in conversations (active window, template-only, closed, and more)',
        'Template sequences for automated follow-up messages',
        'Start new WhatsApp conversations using templates',
        'WhatsApp template campaigns for outbound messaging',
        'Campaign rate limits and cooldown settings to protect quality',
        'Background jobs to monitor sessions and run sequences',
        'Quick template search while composing a message',
        'Template preview with personalized values',
      ],
      improvements: [
        'WhatsApp Templates added to the main sidebar navigation',
        'Campaign creation includes template selection and variable mapping',
        'Warnings when a WhatsApp session is about to expire',
        'Helpful template suggestions based on the current session state',
        'Contact WhatsApp opt-in and quality rating information is easier to manage',
        'Clearer errors when a template does not meet WhatsApp rules',
      ],
      bugFixes: [
        'Fixed templates not saving or loading with the correct status',
        'Fixed language settings on templates not matching WhatsApp correctly',
        'Fixed WhatsApp Templates page not appearing in some builds',
        'Fixed template header, body, footer, and button details in responses',
        'Fixed campaigns treating template approval status incorrectly',
        'Fixed navigation to the WhatsApp Templates page',
        'Fixed campaign runs not tracking template approval status properly',
      ],
    },
    {
      version: '3.2.0',
      date: '2025-12-04',
      type: 'minor',
      features: [
        'Co-Pilot can connect to remote MCP tools over the web',
        'Support for cloud-hosted MCP services',
        'Automatic detection of local vs remote MCP connection types',
        'AI can automatically take over again when a conversation is resolved',
        'Smoother AI-to-human and human-to-AI handoff when resolving chats',
        'Account-level switch to turn auto-escalation on or off',
        'Sentiment-based escalation when customers sound frustrated',
        'All escalation triggers available: low confidence, sentiment, repetition, customer request, and max interactions',
      ],
      improvements: [
        'More reliable connections to remote Co-Pilot tools',
        'Clearer errors and diagnostics when a remote tool cannot connect',
        'Smarter AI handoff when conversation status changes',
        'Default escalation rules are created for new accounts to avoid setup gaps',
        'Interaction-count escalation is now included in auto-escalation checks',
      ],
      bugFixes: [
        'Fixed remote MCP tool setup being rejected incorrectly',
        'Fixed connection errors when configuring web-based MCP tools',
        'Fixed AI not turning back on after a conversation was resolved',
        'Fixed sentiment escalation never triggering',
        'Fixed max-interaction escalation being skipped',
        'Fixed crashes when escalation rules were missing for an account',
      ],
    },
    {
      version: '2.0.0',
      date: '2025-11-22',
      type: 'major',
      features: [
        'Submit and track support tickets from inside Converse',
        'Book a product demo with the sales team',
        'In-app API documentation with examples',
        'Help Center with guided sections for common tasks',
        'Changelog page to follow product updates',
      ],
      improvements: [
        'Richer Reports section with more analytics dashboards',
        'Clearer, more professional email notification templates',
        'Better form error messages across the app',
        'Updated contact details in Help & Support',
      ],
      bugFixes: [
        'Fixed errors on the conversation quality scoring screen',
        'Fixed broken layout pieces on SLA compliance tracking',
        'Fixed Settings page state issues that blocked some actions',
        'Fixed support ticket type mismatches that blocked ticket creation',
      ],
    },
    {
      version: '1.3.0',
      date: '2025-11-18',
      type: 'minor',
      features: [
        'Customer satisfaction (CSAT) analytics dashboard',
        'Sentiment insights from customer feedback',
        'CSAT response tracking and reporting',
        'Visual breakdown of satisfaction ratings',
      ],
      improvements: [
        'CSAT surveys work more smoothly in the chat widget',
        'Clearer response-time tracking',
        'Easier-to-read satisfaction charts and metrics',
      ],
      bugFixes: [
        'Fixed CSAT survey submission failures',
        'Fixed widget loading problems',
        'Fixed inaccurate satisfaction rating calculations',
      ],
    },
    {
      version: '1.2.0',
      date: '2025-11-17',
      type: 'minor',
      features: [
        'Role-based access control for users and teams',
        'Custom roles with fine-grained permissions',
        'Team-based access control',
        'Activity logs and audit history',
      ],
      improvements: [
        'Screens and actions respect each user’s permissions',
        'Clearer user management experience',
        'Better support for team collaboration',
      ],
      bugFixes: [
        'Fixed permission inheritance not applying correctly',
        'Fixed conflicts when assigning roles',
        'Fixed incorrect timestamps in audit logs',
      ],
      security: [
        'Stronger controls so users only see data they are allowed to access',
        'Permission checks enforced before sensitive actions',
        'Hardened sign-in token security',
      ],
    },
    {
      version: '1.1.0',
      date: '2025-11-07',
      type: 'minor',
      features: [
        'Email templates with personalized fields',
        'Rich text editor for writing emails',
        'Template library with ready-to-use examples',
        'Preview emails before you send them',
      ],
      improvements: [
        'More reliable email delivery',
        'Easier organization of templates',
        'More accurate personalization in template fields',
      ],
      bugFixes: [
        'Fixed email formatting problems',
        'Fixed templates not rendering correctly',
        'Fixed issues with email attachments',
      ],
    },
    {
      version: '1.0.0',
      date: '2025-11-03',
      type: 'major',
      features: [
        'Converse AI platform launch',
        'Support customers on Website, WhatsApp, Instagram, Email, and SMS',
        'Real-time conversation management',
        'AI-powered auto-replies',
        'AI Co-Pilot to help agents respond faster',
        'Conversation summaries and sentiment insights',
        'Intent detection to classify customer requests',
        'Knowledge Base to power answers',
        'Automation rules and workflows',
        'Team management and conversation assignment',
        'Contact management with custom fields',
        'Canned responses library',
        'Labels to organize conversations',
        'Business hours configuration',
        'Basic reporting and analytics',
      ],
      improvements: [
        'Fast, reliable live messaging',
        'Works well on desktop and mobile',
        'Quick message delivery for agents and customers',
        'Built to scale for larger teams',
      ],
      bugFixes: [],
      breaking: [
        'First public release of the platform',
      ],
    },
    {
      version: '0.9.0',
      date: '2025-10-20',
      type: 'minor',
      features: [
        'Knowledge Base management',
        'Create and edit help articles',
        'Organize articles into categories',
        'Search across your knowledge content',
      ],
      improvements: [
        'More relevant search results',
        'Clearer content organization',
      ],
      bugFixes: [
        'Fixed article formatting issues',
        'Fixed search not finding some articles',
      ],
    },
    {
      version: '0.8.0',
      date: '2025-10-16',
      type: 'minor',
      features: [
        'Turn AI on or off for individual conversations',
        'Choose when AI assists on a chat-by-chat basis',
        'AI usage analytics',
      ],
      improvements: [
        'Better AI reply quality',
        'Faster AI responses',
      ],
      bugFixes: [
        'Fixed AI on/off setting not saving',
        'Fixed conversation status getting out of sync',
      ],
    },
  ];

  const filteredChangelog = changelogData.filter((entry) =>
    entry.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.features.some(f => f.toLowerCase().includes(searchTerm.toLowerCase())) ||
    entry.improvements.some(i => i.toLowerCase().includes(searchTerm.toLowerCase())) ||
    entry.bugFixes.some(b => b.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const currentEntry = filteredChangelog[0];
  const currentEntrySummary = currentEntry
    ? (currentEntry.features[0] || currentEntry.improvements[0] || currentEntry.bugFixes[0] || '')
        .split(/\s+[—–-]\s+/)[0]
        .trim()
    : '';
  const currentEntryDate = currentEntry
    ? new Date(currentEntry.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const getVersionColor = (type: 'major' | 'minor' | 'patch') => {
    switch (type) {
      case 'major':
        return '#f44336';
      case 'minor':
        return '#2196f3';
      case 'patch':
        return '#4caf50';
      default:
        return '#757575';
    }
  };

  const getVersionLabel = (type: 'major' | 'minor' | 'patch') => {
    switch (type) {
      case 'major':
        return 'MAJOR RELEASE';
      case 'minor':
        return 'MINOR RELEASE';
      case 'patch':
        return 'PATCH';
      default:
        return 'RELEASE';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, color: 'text.primary' }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard/settings')}
          sx={{ textTransform: 'none' }}
        >
          Back to Settings
        </Button>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography color="text.primary">Changelog</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          <Update sx={{ fontSize: 40, mr: 1, verticalAlign: 'middle' }} />
          Changelog
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Stay updated with all the new features, improvements, and bug fixes
        </Typography>
      </Box>

      {/* Current Version Banner */}
      {currentEntry && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Current Version: v{currentEntry.version}
              </Typography>
              <Typography variant="body2">
                Released on {currentEntryDate}
                {currentEntrySummary ? ` — ${currentEntrySummary}` : ''}
                {' '}({getVersionLabel(currentEntry.type).toLowerCase()})
              </Typography>
            </Box>
            <Button variant="contained" endIcon={<ArrowForward />} onClick={scrollToLatest} sx={{ ml: 2, flexShrink: 0 }}>
              View Release
            </Button>
          </Box>
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search changelog (version, features, fixes...)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mb: 4, bgcolor: 'hsl(var(--muted) / 0.5)' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Legend
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberNew sx={{ color: 'info.main' }} />
              <Typography variant="body2">New Features</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed sx={{ color: 'success.main' }} />
              <Typography variant="body2">Improvements</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BugReport sx={{ color: 'warning.main' }} />
              <Typography variant="body2">Bug Fixes</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ color: 'error.main' }} />
              <Typography variant="body2">Security</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Changelog Entries */}
      {filteredChangelog.length === 0 ? (
        <Alert severity="info">
          No changelog entries match your search. Try different keywords.
        </Alert>
      ) : (
        filteredChangelog.map((entry, index) => (
          <Card key={index} ref={index === 0 ? latestRef : undefined} sx={{ mb: 3 }}>
            <CardContent>
              {/* Version Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  v{entry.version}
                </Typography>
                <Chip
                  label={getVersionLabel(entry.type)}
                  sx={{
                    bgcolor: getVersionColor(entry.type),
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {new Date(entry.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Features */}
              {entry.features.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberNew sx={{ color: 'info.main' }} />
                    New Features
                  </Typography>
                  <List dense>
                    {entry.features.map((feature, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: 'info.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Improvements */}
              {entry.improvements.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed sx={{ color: 'success.main' }} />
                    Improvements
                  </Typography>
                  <List dense>
                    {entry.improvements.map((improvement, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={improvement} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Bug Fixes */}
              {entry.bugFixes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReport sx={{ color: 'warning.main' }} />
                    Bug Fixes
                  </Typography>
                  <List dense>
                    {entry.bugFixes.map((fix, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: 'warning.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={fix} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Security */}
              {entry.security && entry.security.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security sx={{ color: 'error.main' }} />
                    Security
                  </Typography>
                  <List dense>
                    {entry.security.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircle fontSize="small" sx={{ color: 'error.main' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Deprecated */}
              {entry.deprecated && entry.deprecated.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteOutline sx={{ color: 'text.secondary' }} />
                    Deprecated
                  </Typography>
                  <List dense>
                    {entry.deprecated.map((item, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <DeleteOutline fontSize="small" sx={{ color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Breaking Changes */}
              {entry.breaking && entry.breaking.length > 0 && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      ⚠️ Breaking Changes
                    </Typography>
                    <List dense>
                      {entry.breaking.map((item, idx) => (
                        <ListItem key={idx} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Build fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Footer */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'hsl(var(--primary) / 0.08)', textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Stay Informed
        </Typography>
        <Typography variant="body1" paragraph>
          Subscribe to our newsletter to get notified about new releases and updates
        </Typography>
        <Button variant="contained" color="primary" size="large">
          Subscribe to Updates
        </Button>
      </Paper>

      {/* Version Roadmap */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          📅 Upcoming Features (Roadmap)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Based on Q2–Q3 2026 delivery plan. Dates are targets, not commitments.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>

          {/* Q2 2026 — Security */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'warning.main' }}>
              🔐 Q2 2026 — Security
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Security sx={{ color: 'warning.main' }} /></ListItemIcon>
                <ListItemText primary="In-transit Encryption (TLS/SSL)" secondary="Target: Jun 2026" />
              </ListItem>
              <ListItem>
                <ListItemIcon><Security sx={{ color: 'warning.main' }} /></ListItemIcon>
                <ListItemText primary="Browser Storage Encryption" secondary="Target: Jun 2026 — localStorage / sessionStorage / IndexedDB" />
              </ListItem>
              <ListItem>
                <ListItemIcon><Security sx={{ color: 'warning.main' }} /></ListItemIcon>
                <ListItemText primary="Database Encryption at Rest" secondary="Target: Jul 2026 — field-level encryption + key management" />
              </ListItem>
              <ListItem>
                <ListItemIcon><Security sx={{ color: 'warning.main' }} /></ListItemIcon>
                <ListItemText primary="Mobile Device Storage Encryption" secondary="Target: Jun 2026" />
              </ListItem>
            </List>
          </Grid>

          {/* Q2 2026 — Integrations & Backend */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'info.main' }}>
              🔌 Q2 2026 — Integrations & Backend
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText primary="Twilio Integration — Production Voice/Video" secondary="Target: Jul 2026 — NAT, scale & call quality for production" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText primary="Create Your Own MCP (WIP)" secondary="Target: Jun 2026 — custom MCP server builder" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText primary="WhatsApp Flow Integration (WIP)" secondary="Target: Jun 2026" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText primary="Voice/Video Phase 8 — WhatsApp Bridge & Mobile Calling" secondary="On Hold — resumes after Twilio + mobile maturity" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'info.main' }} /></ListItemIcon>
                <ListItemText primary="AI-in-Box Integration" secondary="Scope & owner TBD" />
              </ListItem>
            </List>
          </Grid>

          {/* Q3 2026 — Platform Evolution */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'success.main' }}>
              🚀 Q3 2026 — Platform Evolution
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'success.main' }} /></ListItemIcon>
                <ListItemText primary="React → Next.js Migration" secondary="Target: Aug 2026 — SSR, SEO, and performance improvements" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'success.main' }} /></ListItemIcon>
                <ListItemText primary="End-User Mobile App (iOS & Android)" secondary="Target: Aug 2026 — needs architecture plan first" />
              </ListItem>
              <ListItem>
                <ListItemIcon><FiberNew sx={{ color: 'success.main' }} /></ListItemIcon>
                <ListItemText primary="Agent Mobile UI/UX Redesign" secondary="Target: Jul 2026" />
              </ListItem>
            </List>
          </Grid>

        </Grid>
      </Paper>
    </Container>
  );
};

export default ChangelogPage;
